import { TrackingService } from '@/services/analytics/TrackingService';
import { useFnbStore, useHomeStore, useShopStore, type languageType } from '@/stores';
import { buildMenuFromBuildingMenus, CONFIG_CONSTANTS, getRequestStaffHelpFromBuildingMenus, useConfigStore } from '@/stores/configStore';
import type { GNBItemType } from '@/types';
import { useCallback, useEffect, useRef } from 'react';

type Options = {
    /** When true: start/restart 10s timer; on expiry hide Home UI (back to screensaver) */
    enabled: boolean;
    onHide?: () => void;
};

/**
 * After Home UI is revealed from screensaver, auto-hide if user is idle for HOME_MENU_AUTO_HIDE_SEC.
 * On hide: end session + language_logs (same as idle timeout → home), then reset UI language to mainLang.
 * Disabled while screensaver-list / staff-help popup is open (those use idleTimeoutSec CountdownButton instead).
 */
export const useHomeMenuAutoHide = ({ enabled, onHide }: Options) => {
    const setHomeMenuVisible = useHomeStore((s) => s.setHomeMenuVisible);
    const updateConfig = useConfigStore((s) => s.updateConfig);
    const setFnbKeyboardLanguage = useFnbStore((s) => s.setKeyboardLanguage);
    const setShopKeyboardLanguage = useShopStore((s) => s.setKeyboardLanguage);
    const timerRef = useRef<number | null>(null);
    const onHideRef = useRef(onHide);
    onHideRef.current = onHide;

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const resetToMainLanguage = useCallback(() => {
        const { config } = useConfigStore.getState();
        const storeLangs = config?.server?.store?.storeLangs;
        const buildingMenus = config?.server?.buildingMenus;
        const ui = config?.ui;
        const mainLanguage =
            (storeLangs?.find((lang) => lang.main_lang)?.code as languageType) || (ui?.mainLanguage as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;

        if (ui?.language === mainLanguage) return;

        const menu: GNBItemType[] = buildMenuFromBuildingMenus(buildingMenus, mainLanguage, mainLanguage);

        void updateConfig({
            ui: {
                ...ui,
                language: mainLanguage,
                menu,
                requestStaffHelp: getRequestStaffHelpFromBuildingMenus(buildingMenus, mainLanguage),
            },
        });

        setFnbKeyboardLanguage(mainLanguage === 'ko' ? 'ko' : 'en');
        setShopKeyboardLanguage(mainLanguage === 'ko' ? 'ko' : 'en');
    }, [updateConfig, setFnbKeyboardLanguage, setShopKeyboardLanguage]);

    const hideHomeMenu = useCallback(() => {
        clearTimer();
        TrackingService.onTimeoutNavigateHome();
        resetToMainLanguage();
        onHideRef.current?.();
        setHomeMenuVisible(false);
    }, [clearTimer, resetToMainLanguage, setHomeMenuVisible]);

    const restartTimer = useCallback(() => {
        clearTimer();
        timerRef.current = window.setTimeout(() => {
            hideHomeMenu();
        }, CONFIG_CONSTANTS.HOME_MENU_AUTO_HIDE_SEC * 1000);
    }, [clearTimer, hideHomeMenu]);

    useEffect(() => {
        if (!enabled) {
            clearTimer();
            return;
        }

        restartTimer();

        const handleInteraction = () => {
            restartTimer();
        };

        document.addEventListener('pointerdown', handleInteraction, true);
        document.addEventListener('touchstart', handleInteraction, true);
        document.addEventListener('keydown', handleInteraction, true);
        document.addEventListener('scroll', handleInteraction, true);

        return () => {
            clearTimer();
            document.removeEventListener('pointerdown', handleInteraction, true);
            document.removeEventListener('touchstart', handleInteraction, true);
            document.removeEventListener('keydown', handleInteraction, true);
            document.removeEventListener('scroll', handleInteraction, true);
        };
    }, [enabled, restartTimer, clearTimer]);
};

export default useHomeMenuAutoHide;
